import React, { useEffect, useState } from 'react';
import {
  CustomModal,
  useCustomToast,
  CustomButton,
  CustomText,
  FormInput,
} from '../../../../components';
import { DirectGoodsRequest } from '../../types';
import DirectGoodsDetailView from '../Views/DirectGoodsDetailView';
import {
  useDownloadFilesDirectGoods,
  useDownloadPDFDirectGoods,
  useAcceptGoodDelivery,
  useRejectGoodDelivery,
  useGenerateGoodDeliveryToken,
  useFetchDirectGoodsById,
} from '../../../../hooks';
import { useGoodGestorDeliveryProcess } from '../../../../utils/validators/direct-good-request/direct-good-delivery-validator';
import styles from '../../../../components/ModalSaveChanges/ModalSaveChanges.module.css';

interface DirectGoodsDetailProps {
  isOpen: boolean;
  OnClose: () => void;
  data?: DirectGoodsRequest;
  refetch?: () => void;
}

const DirectGoodsDetailContainer: React.FC<DirectGoodsDetailProps> = ({
  isOpen,
  OnClose,
  data,
  refetch,
}) => {
  const [footerLeft, setFooterLeft] = React.useState<React.ReactNode>(null);
  const { mutateAsync: downloadFiles, isPending: isPendingFiles } =
    useDownloadFilesDirectGoods();
  const { mutateAsync: downloadPDF, isPending: isPendingPdf } =
    useDownloadPDFDirectGoods();
  const {
    data: detailData,
    isPending: isLoadingData,
    refetch: refetchById,
  } = useFetchDirectGoodsById(Number(data?.id));
  const {
    mutateAsync: acceptGoodDelivery,
    isPending: isPendingAcceptDelivery,
  } = useAcceptGoodDelivery();
  const {
    mutateAsync: rejectGoodDelivery,
    isPending: isPendingRejectDelivery,
  } = useRejectGoodDelivery();
  const { mutateAsync: generateGoodDeliveryToken, isPending: isPendingToken } =
    useGenerateGoodDeliveryToken();
  const { showToast } = useCustomToast();

  const [isOpenConfirmDelivery, setIsOpenConfirmDelivery] = useState(false);
  const [isOpenTokenModal, setIsOpenTokenModal] = useState(false);
  const [deliveryAction, setDeliveryAction] = useState<
    'accept' | 'reject' | null
  >(null);

  const closeAndReset = () => {
    formikEntrega.resetForm();
    OnClose();
  };

  const onReset = () => {
    formikEntrega.resetForm();
    setTimeout(() => {
      OnClose();
    }, 100);
  };

  const onSaveConfirmDelivery = async (values: any) => {
    try {
      const body = {
        token: values.token,
        portal_observations: values.observations,
      };
      if (values.isAccepting) {
        await acceptGoodDelivery({
          body,
          petition_id: Number(data?.id),
        });
      } else {
        await rejectGoodDelivery({
          body,
          petition_id: Number(data?.id),
        });
      }

      if (refetch) {
        refetch();
      }
      onReset();
    } catch (error: any) {
      if (error.response.statusText === 'Forbidden') {
        showToast('error', 'No tienes Autorizacion');
      } else {
        showToast(
          'error',
          String(error?.message) || 'Error al gestionar el proceso de entrega'
        );
      }
    }
  };

  const onGenerateDeliveryToken = async () => {
    try {
      await generateGoodDeliveryToken(Number(data?.id));
    } catch (error: any) {
      if (error.response.statusText === 'Forbidden') {
        showToast('error', 'No tienes Autorizacion');
      } else {
        showToast(
          'error',
          String(error?.message) || 'Error al obtener el token para la entrega'
        );
      }
    }
  };

  const formikEntrega = useGoodGestorDeliveryProcess({
    onSubmit: onSaveConfirmDelivery,
  });

  const handleOpenConfirm = (action: 'accept' | 'reject') => {
    setDeliveryAction(action);
    formikEntrega.setFieldValue('isAccepting', action === 'accept');
    setIsOpenConfirmDelivery(true);
  };

  const onConfirmAction = async () => {
    const errors = await formikEntrega.validateForm();
    if (errors.observations) {
      formikEntrega.setTouched({
        ...formikEntrega.touched,
        observations: true,
      });
      showToast('error', 'Las observaciones son requeridas');
      return;
    }
    if (deliveryAction === 'accept') {
      formikEntrega.setFieldValue('token', null);
      formikEntrega.setFieldTouched('token', false);
      if (detailData?.actions?.has_signature) {
        await onGenerateDeliveryToken();
        setIsOpenConfirmDelivery(false);
        setIsOpenTokenModal(true);
      } else {
        await onSaveConfirmDelivery(formikEntrega.values);
      }
    } else {
      formikEntrega.handleSubmit();
    }
  };

  const isEnTramiteEntrega = Number(data?.status_id) === 82;
  useEffect(() => {
    if (data?.id) {
      refetchById();
    }
  }, [detailData, data]);
  return (
    <CustomModal
      title={`Detalle solicitud de bienes - ${data?.ticket || ''}`}
      open={isOpen}
      onSave={() => {}}
      onCancel={closeAndReset}
      width="95vw"
      isLoading={isLoadingData}
      footerLeft={footerLeft}
      labelCancel={'Finalizar'}
      labelSubmit={''}
      footerRight={
        isEnTramiteEntrega ? (
          <>
            <CustomButton
              label="Rechazar"
              buttonType="secondary"
              onClick={() => handleOpenConfirm('reject')}
              size="middle"
              style={{ marginRight: '8px' }}
            />
            <CustomButton
              label="Aceptar"
              onClick={() => handleOpenConfirm('accept')}
              size="middle"
            />
          </>
        ) : undefined
      }
    >
      <DirectGoodsDetailView
        detailData={detailData}
        setFooterLeft={setFooterLeft}
        onDocuments={downloadFiles}
        onDownloadPDf={downloadPDF}
        isPendingFiles={isPendingFiles}
        isPendingPdf={isPendingPdf}
        isEnTramiteEntrega={isEnTramiteEntrega}
        formikEntrega={formikEntrega}
      />

      <CustomModal
        title="Confirmación"
        open={isOpenConfirmDelivery}
        labelSubmit="Aceptar"
        onSave={onConfirmAction}
        onCancel={() => setIsOpenConfirmDelivery(false)}
        isLoadingSave={
          isPendingRejectDelivery || isPendingAcceptDelivery || isPendingToken
        }
        disabledBtn={
          isPendingRejectDelivery || isPendingAcceptDelivery || isPendingToken
        }
        disabledCancel={
          isPendingRejectDelivery || isPendingAcceptDelivery || isPendingToken
        }
      >
        <div className={`${styles.Container} pb-24`}>
          <img src="/ModalValidator.svg" className={styles.Container__Img} />
          <CustomText
            customClass={styles.Containe__CustomText}
            text={
              deliveryAction === 'accept'
                ? '¿Desea aceptar y finalizar el proceso del ticket? Se enviará un token al correo del usuario correspondiente para validar la firma electrónica (si el documento lo requiere).'
                : '¿Desea rechazar el proceso del ticket?'
            }
          />
        </div>
      </CustomModal>
      <CustomModal
        title=""
        open={isOpenTokenModal}
        labelSubmit="Firmar"
        onSave={formikEntrega.handleSubmit}
        onCancel={() => {
          setIsOpenTokenModal(false);
        }}
        isLoadingSave={isPendingAcceptDelivery}
        disabledBtn={isPendingAcceptDelivery}
        disabledCancel={isPendingAcceptDelivery}
      >
        <FormInput
          label={'Ingrese token recibido'}
          name="token"
          placeholder="Ingrese el token"
          value={formikEntrega.values.token ?? ''}
          onChange={formikEntrega.handleChange}
          onBlur={formikEntrega.handleBlur}
          error={formikEntrega.errors.token}
          touched={formikEntrega.touched.token}
        />
      </CustomModal>
    </CustomModal>
  );
};

export default DirectGoodsDetailContainer;
